import {
  isSuggestionPopupOpen,
  setSuggestionPopupOpen,
  setCloseHandler,
  clearCloseHandler
} from '../../suggestion-popup-state'
import type { Editor } from '@tiptap/core'
import { ReactRenderer } from '@tiptap/react'
import { SuggestionKeyDownProps } from '@tiptap/suggestion'
import tippy, { GetReferenceClientRect, Instance, Props } from 'tippy.js'
import type * as React from 'react'

type ListHandle = { onKeyDown: (args: SuggestionKeyDownProps) => boolean }

type RendererInstance = {
  element: HTMLElement
  ref: ListHandle | null
  destroy: () => void
  updateProps: (props: unknown) => void
}

const TippyRenderer = ReactRenderer as unknown as new (
  component: unknown,
  options: { props: unknown; editor: unknown }
) => RendererInstance

/**
 * Build the tippy-based render() object used by both slash suggestions,
 * mirroring src/components/PostEditor/PostTextarea/Mention/suggestion.ts.
 */
export function createSuggestionRender<P extends { items: unknown[] }>(
  List: React.ComponentType<any>
) {
  return () => {
    let component: RendererInstance | undefined
    let popup: Instance[] = []
    let touchListener: (e: TouchEvent) => void

    return {
      onBeforeStart: () => {
        touchListener = (e: TouchEvent) => {
          if (popup && popup[0] && isSuggestionPopupOpen()) {
            const popupElement = popup[0].popper
            if (popupElement && !popupElement.contains(e.target as Node)) {
              popup[0].hide()
            }
          }
        }
        document.addEventListener('touchstart', touchListener)

        setCloseHandler(() => {
          if (popup && popup[0]) popup[0].hide()
        })
      },
      onStart: (props: { editor: Editor; clientRect?: (() => DOMRect | null) | null }) => {
        component = new TippyRenderer(List, {
          props,
          editor: props.editor
        })

        if (!props.clientRect) return

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as GetReferenceClientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          hideOnClick: true,
          touch: true,
          onShow() {
            setSuggestionPopupOpen(true)
          },
          onHide() {
            setSuggestionPopupOpen(false)
          }
        })
      },

      onUpdate(props: { clientRect?: (() => DOMRect | null) | null | undefined }) {
        component?.updateProps(props as Partial<P>)

        if (!props.clientRect) return

        popup[0]?.setProps({
          getReferenceClientRect: props.clientRect
        } as Partial<Props>)
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        if (props.event.key === 'Escape') {
          popup[0]?.hide()
          return true
        }
        return component?.ref?.onKeyDown(props) ?? false
      },

      onExit() {
        setSuggestionPopupOpen(false)
        clearCloseHandler()
        popup[0]?.destroy()
        component?.destroy()

        document.removeEventListener('touchstart', touchListener)
      }
    }
  }
}
